/** A plain JSON-like object taken from a poster history snapshot. */
export type SnapshotRecord = Record<string, unknown>;

/**
 * Leaf-level changes to one record. `set` is a nested partial that is deep-merged into the
 * target; `unset` lists the key paths to delete. Arrays are leaves and are replaced whole.
 */
export interface RecordChanges {
  set: SnapshotRecord;
  unset: string[][];
}

export enum PosterOpType {
  POSTER_SET = 'poster.set',
  PAGE_ADD = 'page.add',
  PAGE_REMOVE = 'page.remove',
  PAGE_SET = 'page.set',
  PAGE_ORDER_SET = 'page.order.set',
  ITEM_ADD = 'item.add',
  ITEM_REMOVE = 'item.remove',
  ITEM_SET = 'item.set',
  ITEMS_ORDER_SET = 'items.order.set',
}

export interface PosterSetOp {
  type: PosterOpType.POSTER_SET;
  changes: RecordChanges;
}

export interface PageAddOp {
  type: PosterOpType.PAGE_ADD;
  pageId: string;
  page: SnapshotRecord;
  /** The page this one is placed after; null places it first. */
  afterPageId: string | null;
}

export interface PageRemoveOp {
  type: PosterOpType.PAGE_REMOVE;
  pageId: string;
}

/** Page-level changes. Never carries `items.itemsHashMap` or `items.itemsOrder`; those have their own ops. */
export interface PageSetOp {
  type: PosterOpType.PAGE_SET;
  pageId: string;
  changes: RecordChanges;
}

export interface PageOrderSetOp {
  type: PosterOpType.PAGE_ORDER_SET;
  pageOrder: string[];
}

export interface ItemAddOp {
  type: PosterOpType.ITEM_ADD;
  pageId: string;
  itemId: string;
  item: SnapshotRecord;
  /** The item this one is stacked directly above; null places it at the bottom. */
  afterItemId: string | null;
}

export interface ItemRemoveOp {
  type: PosterOpType.ITEM_REMOVE;
  pageId: string;
  itemId: string;
}

export interface ItemSetOp {
  type: PosterOpType.ITEM_SET;
  pageId: string;
  itemId: string;
  changes: RecordChanges;
}

export interface ItemsOrderSetOp {
  type: PosterOpType.ITEMS_ORDER_SET;
  pageId: string;
  itemsOrder: string[];
}

export type PosterOp = PosterSetOp | PageAddOp | PageRemoveOp | PageSetOp | PageOrderSetOp | ItemAddOp | ItemRemoveOp | ItemSetOp | ItemsOrderSetOp;
